/**
 * Quick Actions Component
 * Quick access cards for common actions - fetched from CMS API
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const CATEGORY_ID = 33;

const ctaColors = [
  'text-purple-600',
  'text-blue-600',
  'text-red-500',
  'text-green-600',
  'text-green-600',
  'text-green-600',
];

// Resolve QuickAction links for specific items regardless of CMS value
const resolveActionLink = (title, fallbackLink) => {
  if (!title) return fallbackLink;
  const normalizedTitle = title.trim().toLowerCase();
  if (normalizedTitle === 'apply for membership') {
    return '/services/become-a-member';
  }
  if (normalizedTitle === 'loan calculator') {
    return '/loan-products#loan-calculator';
  }
  if (normalizedTitle === 'emergency loan') {
    return '/loan-products#emergency-loan';
  }
  if (normalizedTitle === 'development loan') {
    return '/loan-products#development-loan';
  }
  if (normalizedTitle === 'education loan') {
    return '/loan-products#education-loan';
  }
  if (normalizedTitle === 'chapchap loan') {
    return '/loan-products#chapchap-loan';
  }
  return fallbackLink;
};

const QuickActions = () => {
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalSlides, setTotalSlides] = useState(1);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Fetch quick actions from CMS API
  useEffect(() => {
    const fetchActions = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/by-category`,
          { CategoryId: CATEGORY_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
          // Filter: only show items that are NOT deleted AND are published
          const filteredItems = response.data.data.filter(
            (item) => item.IsDeleted === false && item.IsPublished === true
          );

          const apiActions = filteredItems.map((item, index) => ({
            id: item.InfoId || index,
            title: item.Title || '',
            description: item.Description || '',
            icon: LucideIcons[item.Image],
            link: resolveActionLink(item.Title, item.Link) || '/',
            cta: item.ButtonText || 'Explore',
            ctaColor: ctaColors[index % ctaColors.length] || 'text-blue-600',
          }));

          setActions(apiActions);
        }
      } catch (error) {
        console.error('Failed to fetch quick actions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActions();
  }, []);

  const getCardMetrics = () => {
    const container = scrollContainerRef.current;
    if (!container) return { cardWidth: 0, gap: 16, cardsPerView: 1, maxScroll: 0 };
    const firstCard = container.querySelector('a');
    const cardWidth = firstCard ? firstCard.offsetWidth : 0;
    const gap = 16; // Tailwind gap-4 = 16px
    const cardsPerView = Math.max(1, Math.floor(container.clientWidth / (cardWidth + gap)));
    const maxScroll = container.scrollWidth - container.clientWidth;
    return { cardWidth, gap, cardsPerView, maxScroll };
  };

  const calculateTotalSlides = () => {
    const { cardWidth, gap, cardsPerView, maxScroll } = getCardMetrics();
    if (cardWidth <= 0 || maxScroll <= 0) {
      setTotalSlides(1);
      setCurrentIndex(0);
      return;
    }
    const totalCards = actions.length;
    // Each slide shows cardsPerView cards, total slides = totalCards - cardsPerView + 1
    const slidesNeeded = Math.max(1, totalCards - cardsPerView + 1);
    setTotalSlides(slidesNeeded);
  };

  const getScrollForIndex = (index) => {
    const { cardWidth, gap } = getCardMetrics();
    return index * (cardWidth + gap);
  };

  const scrollToIndex = (index) => {
    const { maxScroll } = getCardMetrics();
    if (maxScroll <= 0) return;
    const clampedIndex = Math.max(0, Math.min(index, totalSlides - 1));
    const targetScroll = Math.min(getScrollForIndex(clampedIndex), maxScroll);
    scrollContainerRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  const scrollByCards = (direction) => {
    const { cardWidth, gap, maxScroll } = getCardMetrics();
    if (cardWidth <= 0) return;
    const scrollAmount = cardWidth + gap;
    const container = scrollContainerRef.current;
    const currentScroll = container.scrollLeft;
    let targetScroll;
    if (direction === 'next') {
      targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
    } else {
      targetScroll = Math.max(currentScroll - scrollAmount, 0);
    }
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { cardWidth, gap, maxScroll } = getCardMetrics();
    if (maxScroll <= 0) {
      setCurrentIndex(0);
      setScrollProgress(0);
      return;
    }
    const scrollLeft = container.scrollLeft;
    const newIndex = Math.round(scrollLeft / (cardWidth + gap));
    setCurrentIndex(Math.max(0, Math.min(newIndex, totalSlides - 1)));
    // Calculate scroll progress percentage
    const progress = (scrollLeft / maxScroll) * 100;
    setScrollProgress(Math.min(100, Math.max(0, progress)));
  };

  const goToPrevious = () => {
    scrollByCards('prev');
  };

  const goToNext = () => {
    scrollByCards('next');
  };

  // Touch handlers for swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > swipeThreshold) {
      goToNext();
    } else if (diff < -swipeThreshold) {
      goToPrevious();
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', calculateTotalSlides);

    // Initial calculation after layout
    // Use a small delay to ensure DOM is fully rendered
    const timer = setTimeout(calculateTotalSlides, 100);

    return () => {
      clearTimeout(timer);
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateTotalSlides);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions.length]);

  if (loading) {
    return (
      <section className="bg-gray-100 py-5">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="flex gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-shrink-0 w-[calc(85%-8px)] sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(16.666%-14px)]">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden h-32 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (actions.length === 0) {
    return null;
  }

  return (
    <section className="bg-gray-100 py-5">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Scrollable Cards Container */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto gap-4 scrollbar-hide snap-x snap-mandatory pb-2 -mx-2 px-2 md:mx-0 md:px-0"
          style={{ scrollSnapType: 'x mandatory' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {actions.map((action) => {
            const Icon = action.icon;
            const isExternal = /^https?:\/\//.test(action.link) || /^mailto:/.test(action.link);
            const Wrapper = isExternal ? 'a' : Link;
            const wrapperProps = isExternal
              ? { href: action.link, target: '_blank', rel: 'noopener noreferrer' }
              : { to: action.link };
            return (
              <Wrapper
                key={action.id}
                {...wrapperProps}
                className="flex-shrink-0 w-[calc(85%-8px)] sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(16.666%-14px)] group snap-start"
              >
                <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-2xl hover:border hover:border-[#ca9a37]/30 cursor-pointer">
                  {/* Golden Header */}
                  <div className="bg-[#ca9a37] px-4 py-3 flex items-center justify-between">
                    <h3 className="font-bold text-white text-sm">{action.title}</h3>
                    {Icon && <Icon className="w-5 h-5 text-white" />}
                  </div>
                  {/* White Body */}
                  <div className="p-4">
                    <p className="text-gray-600 text-sm mb-4 min-h-[48px] leading-relaxed">{action.description?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ') || ''}</p>
                    <div className={`${action.ctaColor} flex items-center text-sm font-semibold group-hover:underline`}>
                      {action.cta}
                      <span className="ml-1">→</span>
                    </div>
                  </div>
                </div>
              </Wrapper>
            );
          })}
        </div>

        {/* Carousel Navigation Controls */}
        <div className="flex items-center justify-center mt-4 md:mt-5 gap-4 md:gap-5">
          {/* Left Arrow */}
          <button
            type="button"
            onClick={goToPrevious}
            className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white border border-gray-200 shadow-sm flex lg:hidden items-center justify-center hover:bg-[#2f3951] hover:border-[#2f3951] hover:shadow-md active:scale-95 transition-all duration-200 ease-out group focus:outline-none focus:ring-2 focus:ring-[#ca9a37]/50"
            aria-label="Previous services"
          >
            <LucideIcons.ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-[#2f3951] group-hover:text-white transition-colors duration-200" />
          </button>

          {/* Scroll Progress Bar */}
          <div className="flex lg:hidden items-center w-36 md:w-52 h-1.5 bg-gray-200/70 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#ca9a37] to-[#d4a84a] rounded-full shadow-sm"
              style={{ width: `${scrollProgress}%`, transition: 'width 0.1s linear' }}
            />
          </div>

          {/* Right Arrow */}
          <button
            type="button"
            onClick={goToNext}
            className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white border border-gray-200 shadow-sm flex lg:hidden items-center justify-center hover:bg-[#2f3951] hover:border-[#2f3951] hover:shadow-md active:scale-95 transition-all duration-200 ease-out group focus:outline-none focus:ring-2 focus:ring-[#ca9a37]/50"
            aria-label="Next services"
          >
            <LucideIcons.ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-[#2f3951] group-hover:text-white transition-colors duration-200" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default QuickActions;
